import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { apiClient } from '../config/apiConfig';
import socketService from '../services/socketService';
import toast from 'react-hot-toast';

const WorkflowContext = createContext();

export const useWorkflow = () => {
  const context = useContext(WorkflowContext);
  if (!context) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
};

export const WorkflowProvider = ({ children }) => {
  const [stages, setStages] = useState({
    waiting_to_start: [],
    recruitment: [],
    live: [],
    payment_report: [],
    finished: []
  });
  const [totals, setTotals] = useState({
    waiting_to_start: 0,
    recruitment: 0,
    live: 0,
    payment_report: 0,
    finished: 0
  });
  const [loading, setLoading] = useState(true);
  const [finishedLoading, setFinishedLoading] = useState(false);
  const [error, setError] = useState(null);
  const [finishedError, setFinishedError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [movingCampaignId, setMovingCampaignId] = useState(null);

  // Deduplicate concurrent fetch calls
  const fetchInFlightRef = useRef(null);

  // Debounce timeout for WebSocket-triggered fetches
  const debounceFetchRef = useRef(null);

  // Derive counts from stages
  const counts = useMemo(() => {
    const c = {};
    for (const [key, list] of Object.entries(stages)) c[key] = list.length;
    return c;
  }, [stages]);

  // Fetch campaigns from API
  const fetchCampaigns = useCallback(async (silent = false) => {
    // If a fetch is already in flight, return the existing promise
    if (fetchInFlightRef.current) return fetchInFlightRef.current;

    const doFetch = async () => {
      try {
        if (!silent) {
          setLoading(true);
          setFinishedLoading(true);
        }
        setError(null);
        setFinishedError(null);

        const [activeResponse, finishedResponse] = await Promise.allSettled([
          apiClient.get('/campaigns/workflow'),
          apiClient.get('/campaigns/workflow/finished')
        ]);

        // Handle active campaigns response
        if (activeResponse.status === 'fulfilled' && activeResponse.value.data.success) {
          setStages(prev => ({
            ...activeResponse.value.data.data.stages,
            finished: prev.finished
          }));
          setTotals(prev => ({
            ...activeResponse.value.data.data.totals,
            finished: prev.finished
          }));

          if (activeResponse.value.data.validation && activeResponse.value.data.validation.corrected > 0) {
            const count = activeResponse.value.data.validation.corrected;
            toast.success(`Validated campaigns and corrected ${count} stage${count > 1 ? 's' : ''}`);
          }
        } else if (activeResponse.status === 'rejected') {
          throw activeResponse.reason;
        }

        // Handle finished campaigns response
        if (finishedResponse.status === 'fulfilled' && finishedResponse.value.data.success) {
          const finishedCampaigns = finishedResponse.value.data.data.campaigns;
          setStages(prev => ({ ...prev, finished: finishedCampaigns }));
          setTotals(prev => ({ ...prev, finished: finishedResponse.value.data.data.total }));
          setFinishedError(null);
        } else if (finishedResponse.status === 'rejected') {
          console.error('Error fetching finished campaigns:', finishedResponse.reason);
          setFinishedError('Failed to load finished campaigns');
        }

      } catch (err) {
        console.error('Error fetching workflow campaigns:', err);
        setError(err.message || 'Failed to load campaigns');
        if (!silent) toast.error('Failed to load campaigns');
      } finally {
        if (!silent) {
          setLoading(false);
          setFinishedLoading(false);
        }
        fetchInFlightRef.current = null;
      }
    };

    fetchInFlightRef.current = doFetch();
    return fetchInFlightRef.current;
  }, []);

  // Debounced fetch for WebSocket events
  const debouncedFetch = useCallback(() => {
    if (debounceFetchRef.current) clearTimeout(debounceFetchRef.current);
    debounceFetchRef.current = setTimeout(() => {
      fetchCampaigns(true);
    }, 500);
  }, [fetchCampaigns]);

  // Transition campaign to different stage
  const transitionCampaign = useCallback(async (campaignId, targetStage, reason = '') => {
    // Find the campaign and its current stage
    let campaignData = null;
    let oldStage = null;
    for (const [key, list] of Object.entries(stages)) {
      const found = list.find(c => c._id === campaignId);
      if (found) { campaignData = found; oldStage = key; break; }
    }
    if (!campaignData || oldStage === targetStage) return;

    // Set loading indicator for the card
    setMovingCampaignId(campaignId);

    // Optimistic update — move card immediately
    const optimisticCampaign = { ...campaignData, workflowStage: targetStage };
    setStages(prev => {
      const next = {};
      for (const stageKey of Object.keys(prev)) {
        next[stageKey] = prev[stageKey].filter(c => c._id !== campaignId);
      }
      next[targetStage] = [...next[targetStage], optimisticCampaign];
      return next;
    });

    try {
      const response = await apiClient.post(`/campaigns/${campaignId}/workflow/transition`, {
        targetStage,
        reason
      });

      // apiClient has validateStatus: status => status < 500, so 4xx don't throw
      if (!response.data?.success) {
        throw new Error(response.data?.message || response.data?.error || `Move failed (${response.status})`);
      }

      // Merge only dashboard-relevant fields from server response
      const serverData = response.data.data;
      setStages(prev => ({
        ...prev,
        [targetStage]: prev[targetStage].map(c =>
          c._id === campaignId ? {
            ...c,
            workflowStage: serverData.workflowStage,
            status: serverData.status,
            dashboardMetrics: serverData.dashboardMetrics,
            requiredCreators: serverData.requiredCreators,
            endDate: serverData.endDate
          } : c
        )
      }));

      toast.success(`Moved to ${targetStage.replace(/_/g, ' ')}`);
    } catch (err) {
      // On ANY error: fetch clean server state, no manual rollback
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to move campaign';
      console.error('[Workflow] Transition failed:', msg);
      toast.error(msg);
      await fetchCampaigns(true);
      throw err;
    } finally {
      setMovingCampaignId(null);
    }
  }, [stages, fetchCampaigns]);

  // Move campaign to "waiting to start" (pause)
  const pauseCampaign = useCallback(async (campaignId) => {
    return transitionCampaign(campaignId, 'waiting_to_start', 'Campaign paused');
  }, [transitionCampaign]);

  // Move campaign to finished
  const finishCampaign = useCallback(async (campaignId) => {
    return transitionCampaign(campaignId, 'finished', 'Campaign completed');
  }, [transitionCampaign]);

  // Archive campaign through the compatibility DELETE endpoint.
  const archiveCampaign = useCallback(async (campaignId) => {
    try {
      await apiClient.delete(`/campaigns/${campaignId}`);

      // On success, remove from state
      setStages(prev => {
        const next = {};
        for (const stageKey of Object.keys(prev)) {
          next[stageKey] = prev[stageKey].filter(c => c._id !== campaignId);
        }
        return next;
      });

      toast.success('Campaign archived successfully');
    } catch (err) {
      console.error('Error archiving campaign:', err);
      toast.error('Failed to archive campaign');
      await fetchCampaigns(true);
      throw err;
    }
  }, [fetchCampaigns]);

  // Export campaign CSV
  const exportCampaign = useCallback(async (campaignId) => {
    try {
      const response = await apiClient.get(`/campaigns/${campaignId}/export`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `campaign-${campaignId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Campaign exported successfully');
    } catch (err) {
      console.error('Error exporting campaign:', err);
      toast.error('Failed to export campaign');
      throw err;
    }
  }, []);

  // WebSocket event handlers
  useEffect(() => {
    // Keep a local ref to movingCampaignId accessible inside handlers
    let currentMovingId = movingCampaignId;

    const handleStageChanged = (data) => {
      // Skip fetch if this is the campaign we're currently moving (already handled optimistically)
      if (data?.campaignId === currentMovingId) return;
      debouncedFetch();
    };

    const handleMetricsUpdated = () => debouncedFetch();
    const handleCampaignDeleted = () => debouncedFetch();
    const handleCampaignUpdated = () => debouncedFetch();

    // Connection status — only refetch on RE-connect
    let hasConnectedBefore = false;
    const handleConnect = () => {
      setIsConnected(true);
      if (hasConnectedBefore) {
        fetchCampaigns(true);
      }
      hasConnectedBefore = true;
    };

    const handleDisconnect = () => {
      setIsConnected(false);
    };

    const unsubscribeStageChanged = socketService.on('campaign:workflow:stage_changed', handleStageChanged);
    const unsubscribeMetricsUpdated = socketService.on('campaign:workflow:metrics_updated', handleMetricsUpdated);
    const unsubscribeCampaignDeleted = socketService.on('campaign:deleted', handleCampaignDeleted);
    const unsubscribeCampaignUpdated = socketService.on('campaign:updated', handleCampaignUpdated);
    const unsubscribeConnect = socketService.onConnect(handleConnect);
    const unsubscribeDisconnect = socketService.onDisconnect(handleDisconnect);

    return () => {
      unsubscribeStageChanged();
      unsubscribeMetricsUpdated();
      unsubscribeCampaignDeleted();
      unsubscribeCampaignUpdated();
      unsubscribeConnect();
      unsubscribeDisconnect();
    };
  }, [fetchCampaigns, debouncedFetch, movingCampaignId]);

  // Initial fetch + periodic refresh
  useEffect(() => {
    fetchCampaigns();
    const interval = setInterval(() => fetchCampaigns(true), 30000);
    return () => clearInterval(interval);
  }, [fetchCampaigns]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceFetchRef.current) clearTimeout(debounceFetchRef.current);
    };
  }, []);

  const value = {
    stages,
    counts,
    totals,
    loading,
    finishedLoading,
    error,
    finishedError,
    isConnected,
    fetchCampaigns,
    transitionCampaign,
    pauseCampaign,
    finishCampaign,
    archiveCampaign,
    exportCampaign,
    movingCampaignId
  };

  return (
    <WorkflowContext.Provider value={value}>
      {children}
    </WorkflowContext.Provider>
  );
};
